import { create } from 'zustand';
import i18n from '../../../i18n';
import { homeApi } from '../api/homeApi';
import type { HomeResponse, HomeUpcomingBooking } from './types';

type HomeStatus = 'idle' | 'loading' | 'success' | 'error';

interface HomeState {
  data: HomeResponse | null;
  status: HomeStatus;
  errorMessage: string | null;
  isFetching: boolean; // concurrent fetch guard

  fetch: () => Promise<void>;
  refresh: () => Promise<void>;
  setUpcomingBooking: (booking: HomeUpcomingBooking | null) => void;
  reset: () => void;
}

const doFetch = async (
  set: (partial: Partial<HomeState>) => void,
  force: boolean,
  get: () => HomeState,
): Promise<void> => {
  if (get().isFetching) return;
  if (!force && get().data !== null) return;

  set({ isFetching: true, status: 'loading', errorMessage: null });

  try {
    const data = await homeApi.getHome();
    set({ data, status: 'success', isFetching: false });
  } catch {
    set({
      status: 'error',
      errorMessage: "Ma'lumotlarni yuklashda xatolik yuz berdi",
      isFetching: false,
    });
  }
};

export const useHomeStore = create<HomeState>((set, get) => ({
  data: null,
  status: 'idle',
  errorMessage: null,
  isFetching: false,

  // Idempotent: data bor bo'lsa qayta fetch qilmaydi
  fetch: () => doFetch(set, false, get),

  // Har doim yangi fetch (tab focus, online event)
  refresh: () => doFetch(set, true, get),

  // Optimistic update: cancel-request muvaffaqiyatli bo'lgach
  setUpcomingBooking: (booking) => {
    const data = get().data;
    if (data === null) return;
    set({ data: { ...data, upcomingBooking: booking } });
  },

  reset: () => set({ data: null, status: 'idle', errorMessage: null, isFetching: false }),
}));

// Til o'zgargach backend Accept-Language yangi qiymati bilan ma'lumotni
// qaytadan chiqarib beradi. Listener modul darajasida ulanadi — komponent
// unmount bo'lganda yo'qolmaydi (foydalanuvchi Profile tabida til o'zgartirsa
// ham Home keyingi marta ochilganda allaqachon yangi data bilan tayyor).
if (typeof window !== 'undefined') {
  i18n.on('languageChanged', () => {
    const state = useHomeStore.getState();
    // Faqat data oldindan yuklangan bo'lsa qaytadan tortib olamiz —
    // birinchi mount yo'q bo'lsa, fetch() o'zi mas'ul.
    if (state.data !== null) {
      void state.refresh();
    }
  });
}
